/**
 * FW26 campaign product card.
 *
 * Owns colour selection, pricing display, swatch fitting and the (+) quick add.
 *
 * Quick add opens Horizon's own quick-add dialog (#quick-add-dialog, rendered
 * site-wide by snippets/quick-add-modal.liquid) - the same window the stock theme
 * uses: centred on desktop, a bottom sheet on phones. Horizon's QuickAddComponent
 * can't be reused directly because it reads the product link from a surrounding
 * <product-card>, which this card replaces; so this mirrors its steps:
 *   1. fetch the product page (pre-set to the colour chosen on the card)
 *   2. take its [data-product-grid-content]
 *   3. on phones, rearrange it into the compact sheet layout, exactly as Horizon does
 *   4. morph it into #quick-add-modal-content and open the dialog
 * Adding to cart, the cart drawer and closing on success are all handled by the
 * dialog's own components.
 *
 * Single-variant products skip the dialog and go straight to cart, as in Horizon.
 * If the dialog is unavailable for any reason, the (+) opens the product page.
 */

/** How long to wait for Horizon's product form before falling back (ms). */
const FW_FORM_TIMEOUT = 3000;

/** Fetched product-page grids, keyed by URL, shared by every card on the page. */
const fwQuickAddCache = new Map();

class FwProductCard extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.variantInput = this.querySelector('[data-fw-variant-id]');
    this.toggle = this.querySelector('[data-fw-toggle]');
    this.image = this.querySelector('.fw-card__img') || this.querySelector('img');

    this.priceEl = this.querySelector('[data-fw-price]');
    this.priceNow = this.querySelector('[data-fw-price-now]');
    this.priceWas = this.querySelector('[data-fw-price-was]');
    this.badge = this.querySelector('[data-fw-badge]');
    this.badgeValue = this.querySelector('[data-fw-badge-value]');

    this.variants = this.#readJSON('[data-fw-variants]') || [];
    this.colors = this.#readJSON('[data-fw-colors]') || {};

    this.hasColor = this.hasAttribute('data-has-color');
    this.productUrl = this.dataset.productUrl || '';
    this.productTitle = this.dataset.productTitle || '';
    this.variantCount = Number(this.dataset.variantCount) || this.variants.length;

    this.color = this.dataset.selectedColor || (this.variants[0] && this.variants[0][1]) || '';

    this.addEventListener('click', this.#onClick);

    this.#setupSwatchFit();
    this.#syncVariantInput();
  }

  disconnectedCallback() {
    this.swatchObserver?.disconnect();
    cancelAnimationFrame(this.fitFrame);
    clearTimeout(this.addedTimeout);
  }

  #readJSON(selector) {
    const node = this.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('[fw-product-card] bad JSON in', selector, error);
      return null;
    }
  }

  /* ---- Swatch fitting -------------------------------------------------------
   * The swatch row must always be a single line. Card width varies a lot, so
   * rather than guess a count per layout, measure: show every rendered swatch,
   * then hide from the end until the row fits. The selected colour is never
   * hidden, and the "+N" link always reports how many colours are not shown. */

  #setupSwatchFit() {
    this.swatchRow = this.querySelector('[data-fw-swatch-row]');
    if (!this.swatchRow) return;

    this.swatchMore = this.swatchRow.querySelector('[data-fw-swatch-more]');
    this.swatchTotal = Number(this.swatchRow.dataset.total) || 0;

    this.#fitSwatches();

    if ('ResizeObserver' in window) {
      this.swatchObserver = new ResizeObserver(() => {
        cancelAnimationFrame(this.fitFrame);
        this.fitFrame = requestAnimationFrame(() => this.#fitSwatches());
      });
      this.swatchObserver.observe(this.swatchRow);
    }
  }

  #fitSwatches() {
    const row = this.swatchRow;
    if (!row || !row.isConnected) return;

    const swatches = [...row.querySelectorAll('[data-fw-swatch]')];
    if (!swatches.length) return;

    swatches.forEach((node) => {
      node.hidden = false;
    });
    this.#updateSwatchMore(swatches);

    const overflows = () => row.scrollWidth > row.clientWidth + 1;

    for (let i = swatches.length - 1; i > 0 && overflows(); i--) {
      if (swatches[i].classList.contains('is-active')) continue;
      swatches[i].hidden = true;
      this.#updateSwatchMore(swatches);
    }
  }

  #updateSwatchMore(swatches) {
    if (!this.swatchMore) return;
    const shown = swatches.filter((node) => !node.hidden).length;
    const extra = Math.max(0, this.swatchTotal - shown);
    this.swatchMore.hidden = extra === 0;
    this.swatchMore.textContent = `+${extra}`;
    this.swatchMore.setAttribute('aria-label', `${extra} more colours`);
  }

  /* ---- Interaction --------------------------------------------------------- */

  #onClick = (event) => {
    const swatch = event.target.closest('[data-fw-swatch]');
    if (swatch && this.contains(swatch)) {
      event.preventDefault();
      this.#selectColor(swatch.dataset.color);
      return;
    }

    const toggle = event.target.closest('[data-fw-toggle]');
    if (toggle && this.contains(toggle)) {
      event.preventDefault();
      if (this.variantCount <= 1) {
        this.#addToCart(this.variants[0]);
      } else {
        this.#openQuickAdd();
      }
    }
  };

  #selectColor(color) {
    if (!color || color === this.color) return;
    this.color = color;

    this.querySelectorAll('[data-fw-swatch]').forEach((node) => {
      const active = node.dataset.color === color;
      node.classList.toggle('is-active', active);
      node.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    this.#syncImage();
    this.#syncPrice();
    this.#syncVariantInput();
  }

  /** Swap the image, keeping the responsive sources intact. */
  #syncImage() {
    const info = this.colors[this.color];
    if (!info || !this.image || !info.u) return;

    if (info.ss) this.image.setAttribute('srcset', info.ss);
    else this.image.removeAttribute('srcset');

    this.image.src = info.u;
  }

  /** Keep price, compare-at and the savings badge in step with the chosen colour. */
  #syncPrice() {
    const info = this.colors[this.color];
    if (!info) return;

    if (this.priceNow && info.p) this.priceNow.textContent = info.p;

    const onSale = Boolean(info.c);

    if (this.priceWas) {
      this.priceWas.textContent = onSale ? info.c : '';
      this.priceWas.hidden = !onSale;
    }

    this.priceEl?.classList.toggle('fw-card__price--sale', onSale);

    if (this.badge) {
      if (this.badgeValue) this.badgeValue.textContent = info.s;
      this.badge.hidden = !onSale;
    }
  }

  /** Point the hidden input at the first available variant in the chosen colour. */
  #syncVariantInput() {
    if (!this.variantInput) return;
    const first =
      this.variants.find((v) => (!this.hasColor || v[1] === this.color) && v[3]) ||
      this.variants.find((v) => !this.hasColor || v[1] === this.color);
    if (first) this.variantInput.value = first[0];
  }

  /* ---- Quick add dialog ---------------------------------------------------- */

  /** Product URL pre-set to the variant matching the colour chosen on the card. */
  #quickAddUrl() {
    if (!this.productUrl) return '';
    const url = new URL(this.productUrl, window.location.origin);
    const variantId = this.variantInput?.value;
    if (variantId) url.searchParams.set('variant', variantId);
    return url.toString();
  }

  #goToProduct() {
    const url = this.#quickAddUrl() || this.productUrl;
    if (url) window.location.href = url;
  }

  async #openQuickAdd() {
    if (this.loadingQuickAdd) return;

    const dialog = document.getElementById('quick-add-dialog');
    const modalContent = document.getElementById('quick-add-modal-content');
    const url = this.#quickAddUrl();

    if (!dialog || !modalContent || !url) {
      this.#goToProduct();
      return;
    }

    this.loadingQuickAdd = true;
    this.toggle?.classList.add('is-loading');
    this.toggle?.setAttribute('aria-busy', 'true');

    try {
      const [{ morph }, { isMobileBreakpoint }] = await Promise.all([
        import('@theme/morph'),
        import('@theme/utilities'),
        customElements.whenDefined('quick-add-dialog'),
      ]);

      let grid = fwQuickAddCache.get(url);

      if (!grid) {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const found = html.querySelector('[data-product-grid-content]');
        if (!found) throw new Error('product grid not found on product page');
        grid = found.cloneNode(true);
        fwQuickAddCache.set(url, grid);
      }

      // Always work on a fresh copy so the cached one stays pristine.
      const content = grid.cloneNode(true);

      if (isMobileBreakpoint() && !this.#arrangeForMobile(content, url)) {
        throw new Error('could not build the mobile quick-add layout');
      }

      morph(modalContent, content);

      if (typeof dialog.showDialog !== 'function') throw new Error('quick-add dialog not ready');
      dialog.showDialog();
    } catch (error) {
      console.warn('[fw-product-card] quick add unavailable, opening the product page instead.', error);
      this.#goToProduct();
    } finally {
      this.loadingQuickAdd = false;
      this.toggle?.classList.remove('is-loading');
      this.toggle?.removeAttribute('aria-busy');
    }
  }

  /**
   * Phones get Horizon's compact bottom-sheet layout: title (linked) and price in a
   * header, then the variant picker and the buy buttons, with the long product
   * details removed. Mirrors QuickAddComponent.updateQuickAddModal.
   * @returns {boolean} false if the product page didn't have the expected parts
   */
  #arrangeForMobile(grid, url) {
    const productDetails = grid.querySelector('.product-details');
    const productForm = grid.querySelector('product-form-component');
    const variantPicker = grid.querySelector('variant-picker');
    const productPrice = grid.querySelector('product-price');

    if (!productDetails || !productForm || !variantPicker || !productPrice) return false;

    const title = document.createElement('a');
    title.textContent = this.productTitle;
    title.href = url;

    const header = document.createElement('div');
    header.classList.add('product-header');
    header.append(title, productPrice);

    grid.append(header, variantPicker, productForm);
    productDetails.remove();
    return true;
  }

  /* ---- Direct add (single-variant products) -------------------------------- */

  async #addToCart(variant) {
    if (!variant || !variant[3] || !this.form || !this.variantInput) return;

    this.variantInput.value = variant[0];
    this.variantInput.disabled = false;

    // Horizon's product form is loaded with a low fetch priority. Wait for it (up
    // to a limit) rather than letting the form fall back to a full-page submit;
    // if it never arrives, submit normally so the click still does something.
    const ready = await this.#formComponentReady();

    if (!ready) {
      this.form.submit();
      return;
    }

    this.form.requestSubmit();
    this.#flashAdded();
  }

  #formComponentReady() {
    if (customElements.get('product-form-component')) return Promise.resolve(true);

    return Promise.race([
      customElements.whenDefined('product-form-component').then(() => true),
      new Promise((resolve) => {
        setTimeout(() => resolve(false), FW_FORM_TIMEOUT);
      }),
    ]);
  }

  #flashAdded() {
    if (!this.toggle || this.addedTimeout) return;
    const glyph = this.toggle.querySelector('[data-fw-glyph]') || this.toggle;
    const original = glyph.textContent;
    glyph.textContent = '✓';
    this.addedTimeout = setTimeout(() => {
      glyph.textContent = original;
      this.addedTimeout = null;
    }, 2000);
  }
}

if (!customElements.get('fw-product-card')) {
  customElements.define('fw-product-card', FwProductCard);
}
